/**
 * CrashDialog - "Oops! Something went wrong" dialog
 *
 * Shows crash information with expandable details and offers
 * "Ignore" or "Send Report" options.
 *
 * PRIVACY: Only displays sanitized context (no credentials, paths, or user data).
 */
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { AlertCircle, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { writeClipboardText } from '../../../lib/clipboard';
import type { CrashContext, CrashDialogAction } from '../types/crashTypes';

/** Format a timestamp as relative time (e.g., "2 mins ago") */
function formatRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

/** Format session age as human readable */
function formatSessionAge(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/** Format crash context as copyable text */
function formatContextForCopy(context: CrashContext): string {
  const { app, error, workspace, connection, metrics, breadcrumbs } = context;

  const lines = [
    'Jam SQL Studio Crash Report',
    '===========================',
    `Version: ${app.version}`,
    `Platform: ${app.platform} / ${app.arch}`,
    '',
    'Error',
    '-----',
    `Category: ${error.category}`,
    `Message: ${error.message}`,
    '',
    'Workspace',
    '---------',
    `Active Tab: ${workspace.activeTabType ?? 'none'}`,
    `Active Tab Engine: ${workspace.activeTabEngine ?? 'none'}`,
    `Open Tabs: ${workspace.tabCount}`,
    '',
    'Connections',
    '-----------',
    `Engines: ${connection.connectionEngines.length > 0 ? connection.connectionEngines.join(', ') : 'none'}`,
    '',
    'Session',
    '-------',
    `Duration: ${formatSessionAge(metrics.sessionAge)}`,
    `Memory: ${metrics.memoryUsageMB > 0 ? `${metrics.memoryUsageMB} MB` : 'N/A'}`,
  ];

  if (breadcrumbs.length > 0) {
    lines.push('', 'Recent Actions', '--------------');
    breadcrumbs.slice(-5).reverse().forEach((bc) => {
      lines.push(`- ${bc.action}: ${bc.target} (${formatRelativeTime(bc.timestamp)})`);
    });
  }

  return lines.join('\n');
}

/** Props for the CrashDialog component */
type CrashDialogActionWithMemory = {
  action: CrashDialogAction;
  remember: boolean;
};

type CrashDialogProps = {
  open: boolean;
  context: CrashContext;
  /**
   * Legacy handler. Still used by the E2E test harness. Must stay wired so
   * existing tests keep passing.
   */
  onAction: (action: CrashDialogAction) => void;
  /**
   * When provided, the dialog also reports whether "Remember my choice" was
   * checked at the time of action. Callers that want to flip the app-wide
   * preference should use this instead of `onAction`.
   */
  onActionWithMemory?: (result: CrashDialogActionWithMemory) => void;
};

/** Expandable details section showing crash context */
function CrashDetails({ context }: { context: CrashContext }) {
  const { app, workspace, connection, metrics, breadcrumbs, error } = context;

  return (
    <div
      className="space-y-3 text-xs font-mono text-muted-foreground"
      data-testid="crash-details"
    >
      {/* App Info */}
      <div>
        <div className="font-semibold text-foreground mb-1">Application</div>
        <div>Version: {app.version}</div>
        <div>Platform: {app.platform} / {app.arch}</div>
      </div>

      {/* Error Info */}
      <div>
        <div className="font-semibold text-foreground mb-1">Error</div>
        <div>Category: {error.category}</div>
        <div className="break-words">Message: {error.message}</div>
      </div>

      {/* Workspace State */}
      <div>
        <div className="font-semibold text-foreground mb-1">Workspace</div>
        <div>Active Tab: {workspace.activeTabType ?? 'none'}</div>
        <div>Active Tab Engine: {workspace.activeTabEngine ?? 'none'}</div>
        <div>Open Tabs: {workspace.tabCount}</div>
      </div>

      {/* Connection State */}
      <div>
        <div className="font-semibold text-foreground mb-1">Connections</div>
        <div>
          Engines: {connection.connectionEngines.length > 0
            ? connection.connectionEngines.join(', ')
            : 'none'}
        </div>
      </div>

      {/* Metrics */}
      <div>
        <div className="font-semibold text-foreground mb-1">Session</div>
        <div>Duration: {formatSessionAge(metrics.sessionAge)}</div>
        <div>Memory: {metrics.memoryUsageMB > 0 ? `${metrics.memoryUsageMB} MB` : 'N/A'}</div>
      </div>

      {/* Recent Actions */}
      {breadcrumbs.length > 0 && (
        <div>
          <div className="font-semibold text-foreground mb-1">
            Recent Actions ({breadcrumbs.length})
          </div>
          <ul className="space-y-0.5 max-h-24 overflow-y-auto">
            {breadcrumbs.slice(-5).reverse().map((bc, idx) => (
              <li key={idx} className="flex justify-between">
                <span>{bc.action}: {bc.target}</span>
                <span className="text-muted-foreground/60 ml-2">
                  {formatRelativeTime(bc.timestamp)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * CrashDialog Component
 *
 * Modal dialog shown when an error occurs. Provides:
 * - Error summary message
 * - Expandable technical details
 * - Ignore (dismiss) action
 * - Send Report action
 */
export function CrashDialog({
  open,
  context,
  onAction,
  onActionWithMemory,
}: CrashDialogProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const [remember, setRemember] = useState(false);

  const fire = (action: CrashDialogAction) => {
    onActionWithMemory?.({ action, remember });
    onAction(action);
  };

  const handleIgnore = () => fire('ignore');
  const handleSendReport = () => fire('send_report');

  const handleCopyDetails = async () => {
    const text = formatContextForCopy(context);
    const ok = await writeClipboardText(text);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      console.error('Failed to copy crash details');
    }
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        className="max-w-md"
        data-testid="crash-dialog"
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
              <AlertCircle className="h-5 w-5" />
            </div>
            <AlertDialogTitle>Oops! Something went wrong</AlertDialogTitle>
          </div>
        </AlertDialogHeader>

	        <AlertDialogDescription asChild>
	          <div className="space-y-4">
	            <p className="text-sm text-muted-foreground">
	              Help us prevent crashes in future versions by sending an anonymous report.
	            </p>

            {/* Expandable Details Section */}
            <div className="rounded-md border border-border">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-2 flex-1 px-3 py-2 text-sm text-left hover:bg-accent/50 rounded-md transition-colors"
                  data-testid="show-details-button"
                >
                  {showDetails ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <span>Show Details</span>
                </button>
                {showDetails && (
                  <button
                    type="button"
                    onClick={handleCopyDetails}
                    className="flex items-center gap-1 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded-md transition-colors"
                    data-testid="copy-details-button"
                    title="Copy crash details to clipboard"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-green-500" />
                        <span className="text-green-500">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {showDetails && (
                <div className="px-3 pb-3 pt-1 border-t border-border">
                  <CrashDetails context={context} />
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="crash-dialog-remember"
                checked={remember}
                onCheckedChange={(next) => setRemember(next === true)}
                data-testid="crash-dialog-remember"
              />
              <Label htmlFor="crash-dialog-remember" className="cursor-pointer text-xs text-muted-foreground">
                Remember my choice
              </Label>
            </div>
          </div>
        </AlertDialogDescription>

	        <AlertDialogFooter>
	          <Button
	            variant="outline"
	            onClick={handleIgnore}
	            data-testid="crash-ignore-button"
	          >
	            Ignore
	          </Button>
	          <Button
	            onClick={handleSendReport}
	            className="bg-blue-600 hover:bg-blue-700 text-white"
	            data-testid="crash-send-report-button"
	          >
	            Send Report
	          </Button>
	        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
